import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AdminHeaderComponent } from './components/admin-header/admin-header.component';
import { ToastComponent } from './components/toast/toast.component';
import { setCanonicalLinkHref } from './core/seo/canonical-link.util';
import { buildCanonicalUrl, parseRouterUrl } from './core/seo/url-routing.utils';
import { resolveAppShellRouteState } from './core/shell/app-shell-route.state';
import { AuthService } from './services/auth.service';
import { BrowserThemeService } from './services/browser-theme.service';
import { UtmService } from './services/utm.service';
import { environment } from '../environments/environment';

/**
 * Panels shell — customer storefront chrome (header/footer/SWF/chat) lives in Next.js.
 */
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AdminHeaderComponent, ToastComponent, CommonModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css', './Seller-Panel/seller-panel-theme.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'ecomerce';
  user: any;
  isAdminPanel = false;
  isAdminDashboardShell = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private themeService: BrowserThemeService,
    private cdr: ChangeDetectorRef,
    private utmService: UtmService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.onRouteChanged(event.urlAfterRedirects || event.url);
      }
    });
    this.onRouteChanged(this.router.url);
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.restoreSession();
    this.themeService.listenForSystemThemeChanges();
  }

  private onRouteChanged(url: string): void {
    this.updateCanonicalUrl(url);
    if (isPlatformBrowser(this.platformId)) {
      this.utmService.captureUtmParameters();
    }
    const shell = resolveAppShellRouteState(url);
    this.isAdminPanel = shell.isAdminPanel;
    this.isAdminDashboardShell = shell.isAdminDashboardShell;
    this.cdr.markForCheck();
  }

  private updateCanonicalUrl(url: string): void {
    const origin = this.resolveSiteOrigin();
    const { path, query } = parseRouterUrl(url);
    setCanonicalLinkHref(this.document, buildCanonicalUrl(origin, path, query));
  }

  private resolveSiteOrigin(): string {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }
    return String(
      (environment as { frontendUrl?: string }).frontendUrl || 'https://merchantsbazaar.com',
    ).replace(/\/$/, '');
  }

  private restoreSession(): void {
    const user = this.authService.getUser();
    if (user) {
      this.authService.setUser(user);
      this.user = user;
    }

    this.authService.isLoggedIn$.subscribe((isLoggedIn) => {
      this.user = isLoggedIn ? this.authService.getUser() : null;
      this.cdr.markForCheck();
    });
  }
}
